import { redirectToLogin } from '@ynap/redirect-utils';
import { createFetchReplay, createGuestIdentity, getWcsConfig, updateUberToken, addCredentials } from './fetch-wcs-util';
import { raygun } from '@ynap/clientside-monitoring';
import { GuestIdentityReissuedError } from './errors';

export const createFetchWcs = (fetch, store, customGetUserSummary) => async (request, allOptions) => {
    const { bypassCreateGuestUbertoken, ...options } = allOptions || {};
    const originalRequest = typeof request === 'string' ? request : request.clone();
    const getUserSummary =
        customGetUserSummary || (typeof window !== 'undefined' && window?.SF?.getUserSummary ? window.SF.getUserSummary : () => Promise.resolve(null));

    const brandId = store.getState()?.config?.global?.application?.brandId;
    const applicationName = store.getState()?.config?.head?.name;
    const applicationVersion = store.getState()?.config?.head?.version;

    let response, message;
    let api = raygun.helpers.getAPIUrl(request.url, brandId);
    const label = request.headers.get('label') ? request.headers.get('label') : api;

    if (applicationName) {
        request.headers.set('application-name', applicationName);
    }

    if (applicationVersion) {
        request.headers.set('application-version', applicationVersion);
    }

    const [requestWithCredentials, optionsWithCredentials] = await addCredentials(request, options);

    try {
        response = await fetch(requestWithCredentials, optionsWithCredentials);
    } catch (err) {
        message = `fetch-wcs - network error - ${label}`;

        raygun.helpers.agent('send', {
            error: new Error(message),
            tags: ['fetch-wcs', `${label}`, `network error`, ...(options?.errorTags || [])],
            customData: { err, api, label },
        });

        throw err;
    }

    const originalResponse = response.clone();

    if (!response.ok) {
        let json;
        try {
            json = await response.json();
        } catch (err) {
            raygun.helpers.agent('send', {
                error: err,
                tags: ['fetch-wcs', `${response.status}`, `${label}`, 'apiParsingError', ...(options?.errorTags || [])],
                customData: { api, label },
            });

            return originalResponse;
        }

        const { errors = [], Ubertoken } = json;
        const [firstError] = errors;

        if (json) {
            message = `fetch-wcs - ${response.status} - ${label} - ${firstError?.errorKey}`;

            raygun.helpers.agent('send', {
                error: new Error(message),
                tags: ['fetch-wcs', `${response.status}`, `${label}`, firstError?.errorKey, ...(options?.errorTags || [])],
                customData: { errors, api, label },
            });
        }

        if (firstError && firstError.errorKey) {
            const state = store.getState();
            const fetchReplay = createFetchReplay(fetch, originalRequest, options);
            const wcsConfig = getWcsConfig(state);

            switch (firstError.errorKey) {
                case '_ERR_EXPIRED_ACTIVITY_TOKEN':
                case '_ERR_TERMINATED_ACTIVITY_TOKEN':
                case 'CWXBB1012E': //Production Error Key, that can't be changed with breaking other heads means the same as ERR_TERMINATED_ACTIVITY_TOKEN
                case '_ERR_INVALID_COOKIE':
                case '_ERR_INVALID_ACTIVITY_TOKEN':
                case 'NOT_AUTHORIZED_FOR_QUERY':
                case '_ERR_ACTIVITY_TOKEN':
                case 'ERR_USER_ACCESS_BEAN':
                case '_ERR_UBF_USER_AUTHORITY': {
                    if (!bypassCreateGuestUbertoken) {
                        const json = await createGuestIdentity(fetch, wcsConfig);
                        const replayResponse = await fetchReplay(json.Ubertoken);
                        updateUberToken(store.dispatch, json.Ubertoken);
                        return replayResponse;
                    } else {
                        return originalResponse;
                    }
                }
                case 'ERR_PARTIAL_AUTHENTICATION_NOT_ALLOWED':
                    return getUserSummary().then(async (user) => {
                        if (user) {
                            updateUberToken(store.dispatch);
                            return options?.customRedirect
                                ? options.customRedirect()
                                : redirectToLogin(state.locale ? `${state.locale.language}-${state.locale.country}`.toLowerCase() : state.appState.locale);
                        } else {
                            if (!bypassCreateGuestUbertoken) {
                                const json = await createGuestIdentity(fetch, wcsConfig, state.uberToken);
                                updateUberToken(store.dispatch, json.Ubertoken);
                                throw new GuestIdentityReissuedError(json.Ubertoken);
                            } else {
                                return originalResponse;
                            }
                        }
                    });

                case 'ERR_AUTHENTICATION_REQUIRED': {
                    // TODO: remove this once WCS fix error code => https://jira.nap/browse/SHOP-1494
                    updateUberToken(store.dispatch);
                    return options?.customRedirect
                        ? options.customRedirect()
                        : redirectToLogin(state.locale ? `${state.locale.language}-${state.locale.country}`.toLowerCase() : state.appState.locale);
                }
                case 'ERR_OLD_ENCRYPTION_KEY':
                case 'ERR_SESSION_AFFINITY':
                    if (Ubertoken) {
                        const replayResponse = await fetchReplay(Ubertoken);
                        updateUberToken(store.dispatch, Ubertoken);
                        return replayResponse;
                    }
            }
        }
    }

    return originalResponse;
};

export const fetchWcs = (fetch, request, options, store) => createFetchWcs(fetch, store)(request, options);
